import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { User } from "../../types/User";
import api from "../../utils/api";

interface PaginatedUsers {
  data: User[];
  totalCount: number;
  totalPages: number;
  currentPage: number;
}

interface AuthState {
  usersByRole: PaginatedUsers;
  user: User | null;
  userByToken: User | null;
  userById: User | null;
  isAuth: boolean;
  boxAccessToken: string | null;
  isLoading: boolean; // this is used for both user and isAuth

  isLoadingUserById: boolean;
  isLoadingAddUpdateUser: boolean;
}

const initialState: AuthState = {
  usersByRole: { data: [], totalCount: 0, totalPages: 1, currentPage: 1 },
  user: null,
  userByToken: null,
  userById: null,
  isAuth: false,
  boxAccessToken: null,
  isLoading: false,

  isLoadingUserById: false,
  isLoadingAddUpdateUser: false,
};

export const fetchUser = createAsyncThunk<
  User | null,
  void,
  { rejectValue: any }
>("auth/fetchUser", async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(`/auth/current_user`, {
      withCredentials: true,
    });
    return res.data;
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      return rejectWithValue(null);
    }
    return rejectWithValue(err.response?.data || "Fetch failed");
  }
});

export const addUpdateUser = createAsyncThunk<
  any,
  { formData: Partial<User> },
  { rejectValue: string }
>("/dashboard/profile", async ({ formData }, { rejectWithValue }) => {
  try {
    const result = await api.put(
      `/auth/profile`,
      formData,
      {
        headers: {
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    );
    return result.data;
  } catch (error: any) {
    return rejectWithValue(
      error.response?.data?.message || "Profile update failed"
    );
  }
});

export const logoutUser = createAsyncThunk("auth/logoutUser", async () => {
  await api.get(`/auth/logout`, {
    withCredentials: true,
  });
});

export const fetchBoxAccessToken = createAsyncThunk<
  string,
  boolean | void,
  { rejectValue: string }
>("auth/fetchBoxAccessToken", async (forceRefresh, thunkAPI) => {
  const { rejectWithValue } = thunkAPI;
  const name = "box_access_token";

  try {
    const res = await api.get(
      `/api/localUploader/get-constants/${name}`,
      {
        withCredentials: true,
        params: forceRefresh ? { refresh: "1" } : undefined,
      }
    );

    const token = res.data?.value as string | null;

    if (!token) {
      return rejectWithValue("Box token not found");
    }

    return token;
  } catch (error: any) {
    console.error("fetchBoxAccessToken error:", error);
    return rejectWithValue(
      error?.response?.data?.message ||
      error.message ||
      "Failed to fetch Box token"
    );
  }
});

export const getUserByUserId = createAsyncThunk<
  User,
  string,
  { rejectValue: string }
>("auth/getUserByUserId", async (user_id, { rejectWithValue }) => {
  try {
    const res = await api.get(
      `/auth/getUserByUserId/${user_id}`
    );

    return res.data?.data as User;
  } catch (error: any) {
    return rejectWithValue(
      error?.response?.data?.message || "Fetch user by token failed"
    );
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.isAuth = false;
      state.boxAccessToken = null;
    },
    setUser(state, action: PayloadAction<User | null>) {
      state.user = action.payload;
      state.isAuth = !!action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUser.pending, (state) => {
        if (!state.user) state.isLoading = true;
      })
      .addCase(
        fetchUser.fulfilled,
        (state, action: PayloadAction<User | null>) => {
          state.isLoading = false;
          state.user = action.payload;
          state.isAuth = !!action.payload;
        }
      )
      .addCase(fetchUser.rejected, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuth = false;
      })

      .addCase(addUpdateUser.pending, (state) => {
        state.isLoadingAddUpdateUser = true;
      })
      .addCase(addUpdateUser.fulfilled, (state, action) => {
        if (action.payload?.user) {
          state.user = action.payload.user;
          state.isAuth = true;
        }
        state.isLoadingAddUpdateUser = false;
      })
      .addCase(addUpdateUser.rejected, (state) => {
        state.isLoadingAddUpdateUser = false;
      })

      .addCase(logoutUser.fulfilled, (state) => {
        state.user = null;
        state.isAuth = false;
        state.isLoading = false;
        state.boxAccessToken = null;
      })

      .addCase(fetchBoxAccessToken.fulfilled, (state, action) => {
        state.boxAccessToken = action.payload;
      })
      .addCase(fetchBoxAccessToken.rejected, (state) => {
        state.boxAccessToken = null;
      })

      .addCase(getUserByUserId.pending, (state) => {
        state.isLoadingUserById = true;
      })
      .addCase(
        getUserByUserId.fulfilled,
        (state, action: PayloadAction<User>) => {
          state.userById = action.payload;
          state.isLoadingUserById = false;
        }
      )
      .addCase(getUserByUserId.rejected, (state) => {
        state.userById = null;
        state.isLoadingUserById = false;
      });
  },
});

export const { logout, setUser } = authSlice.actions;
export default authSlice.reducer;